<template>
  <RenderlessCalendar
    v-slot="slotProps"
    :custom-number-of-months="numberOfMonths"
    :locale="calendarLocale"
    :min-date="minDate"
    :max-date="maxDate"
    :default-selected-dates="selectedDates"
    :marked-dates="markedDates"
    view-mode="custom"
    @onDateChange="handleDateChange"
  >
    <section ref="scrollableCalendarRef" class="space-y-2">
      <div
        v-for="(view, idx) in cropMonths(slotProps.calendar)"
        :key="`${view.year}-${view.month}`"
        class="js-calendar-month bg-background-neutral-secondary scroll-mt-5 rounded-xl p-4"
        :data-month-key="`${view.year}-${view.month}`"
        data-qa-id="calendarSheet.monthBlock"
      >
        <p class="text-text-neutral-primary font-semibold">
          {{
            getMonthName({
              monthNames: slotProps.monthNames,
              monthId: view.month,
              year: view.year,
            })
          }}
        </p>
        <div class="flex flex-wrap justify-between space-y-1">
          <!-- first:mt-1 is needed since space-y-1 does not for the first, but needed to be aligned -->
          <CalendarCell
            v-for="(date, i) in view.dates"
            :key="date.ms"
            :date="date"
            :is-loading="isLoading"
            class="first:mt-1"
            v-bind="{
              ...getNecessaryCellModifiers(slotProps.getModifiers(date), date),
              ...getFirstLastDateOfMonthModifiers(view.dates, i),
              ...getDatePriceModifiers(date),
              isRange: selectedDates.length > 1,
            }"
            @onSelect="slotProps.onDateSelect(date)"
            @onDisabledSelect="emit('onDisabledDateSelect')"
          >
            <template
              v-if="isNewCalendarVersion"
              #datePrice="{ datePrice, isSelected, isCheapest, isMarked }"
            >
              <span
                v-if="
                  !isSelected &&
                  !date.isOtherMonthDay &&
                  !isLoading &&
                  !isMarked
                "
                class="text-2xs absolute bottom-0 w-full text-center font-medium"
                :class="{
                  'text-text-info-primary': isFiltered,
                  'text-text-positive-primary': !isFiltered && isCheapest,
                  'text-text-neutral-quaternary': !isFiltered && !isCheapest,
                }"
              >
                {{ datePrice }}
              </span>
            </template>
          </CalendarCell>
        </div>
        <!-- Lazy loading to optimize initial rendering -->
        <IntersectionPoint
          v-if="idx === calendarLazyLoader.visibleItems.value - 1"
          @onIntersect="onLazyLoaderIntersect"
        />
      </div>
    </section>
  </RenderlessCalendar>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { defer } from 'lodash-es';
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  import ruLocale from 'vue-renderless-calendar/dist/locale/ru'; // No type declaration for ruLocale https://github.com/winexy/vue-renderless-calendar/issues/34
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  import kzLocale from 'vue-renderless-calendar/dist/locale/kz'; // No type declaration for ruLocale https://github.com/winexy/vue-renderless-calendar/issues/34
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  import enLocale from 'vue-renderless-calendar/dist/locale/en'; // No type declaration for ruLocale https://github.com/winexy/vue-renderless-calendar/issues/34
  import { appLocale, searchFormCalendar, IntersectionPoint } from 'shared';
  import { RenderlessCalendar } from 'vue-renderless-calendar';
  import type { CalendarDate } from 'vue-renderless-calendar';

  import CalendarCell from './CalendarCell.vue';
  import type { CalendarCellModifiers, CalendarPrices } from '../../types';

  type GetMonthNameParams = {
    monthNames: Array<{ full: string }>;
    monthId: number;
    year: string;
  };

  const props = defineProps({
    mode: {
      type: String as () => searchFormCalendar.config.CalendarMode,
      default: () => searchFormCalendar.config.CALENDAR_MODES.SINGLE,
    },
    maxDate: {
      type: String,
      default: '',
    },
    minDate: {
      type: String,
      default: '',
    },
    prices: {
      type: Object as () => CalendarPrices | null,
      default: () => null,
    },
    numberOfMonths: {
      type: Number,
      required: true,
    },
    startDate: {
      type: Object as () => { month: number; year: number },
      default: null,
    },
    selectedDates: {
      type: Array<string>,
      default: () => [],
    },
    markedDates: {
      type: Array<string>,
      default: () => [],
    },
    availableDates: {
      type: Array<string>,
      default: undefined,
    },
    isNewCalendarVersion: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isFiltered: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits<{
    onDateChange: [date: string];
    onDisabledDateSelect: [];
    onCalendarIntersect: [visibleItems: number];
  }>();

  const scrollableCalendarRef = ref();

  const { lazyLoader: calendarLazyLoader } =
    searchFormCalendar.model.useCalendarInitialScroll({
      calendarRef: scrollableCalendarRef,
      selectedDates: props.selectedDates,
      minDate: props.minDate,
      markedDates: props.markedDates,
    });

  const calendarLocale = computed(() => {
    const { locale } = useI18n();
    if (locale.value === appLocale.config.locales.RU) {
      return ruLocale;
    }
    if (locale.value === appLocale.config.locales.KK) {
      return kzLocale;
    }
    return enLocale;
  });

  const cheapestPrice = computed(() => {
    const values = Object.values(props.prices ?? {}).map(value =>
      Number(value.price),
    );
    return Math.min(...values);
  });

  const cheapestPricesPerMonth = ref<Record<number, number>>({});

  const calculateCheapestPricePerMonth = (prices: CalendarPrices | null) => {
    if (!prices) {
      return {};
    }
    const cheapestPricesPerOneMonth: Record<number, number[]> = {};

    Object.entries(prices).forEach(([key, value]) => {
      const month = Number(key.split('-')[1]);
      const price = Number(value.price);

      if (!cheapestPricesPerOneMonth[month]) {
        cheapestPricesPerOneMonth[month] = [];
      }
      cheapestPricesPerOneMonth[month].push(price);
    });

    cheapestPricesPerMonth.value = Object.fromEntries(
      Object.entries(cheapestPricesPerOneMonth).map(([month, prices]) => [
        Number(month),
        Math.min(...prices),
      ]),
    );
  };

  const getDatePriceModifiers = (date: CalendarDate) => {
    const [firstDate, secondDate] = props.selectedDates;
    let priceKey =
      secondDate ||
      props.mode === searchFormCalendar.config.CALENDAR_MODES.RANGE
        ? `${firstDate}:${date.formatted}`
        : date.formatted;

    if (props.isNewCalendarVersion) {
      priceKey = date.formatted;
      const price = props.prices?.[priceKey]?.price;
      return {
        price,
        isCheapest: price
          ? cheapestPricesPerMonth.value[date.actualMonthNumber] === +price
          : false,
        isFiltered: props.isFiltered,
      };
    }
    const price = props.prices?.[priceKey]?.price;

    return {
      price,
      isCheapest: price ? Number(price) === cheapestPrice.value : false,
    };
  };
  const handleDateChange = ([date]: Array<CalendarDate>) =>
    emit('onDateChange', date.formatted);
  const getMonthName = ({ monthNames, monthId, year }: GetMonthNameParams) =>
    `${monthNames[monthId].full} ${year}`;
  const getFirstLastDateOfMonthModifiers = (
    dates: Array<CalendarDate>,
    currentDateIndex: number,
  ) => {
    const prev = dates[currentDateIndex - 1];
    const cur = dates[currentDateIndex];
    const next = dates[currentDateIndex + 1];
    return {
      isFirstDateOfMonth:
        !cur.isOtherMonthDay && (!prev || prev.isOtherMonthDay),
      isLastDateOfMonth:
        !cur.isOtherMonthDay && (!next || next.isOtherMonthDay),
    };
  };
  const getNecessaryCellModifiers = (
    {
      isLast,
      isFirst,
      isBetween,
      isSelected,
      isDisabled,
      isMarked,
    }: CalendarCellModifiers,
    date: CalendarDate,
  ) => {
    return {
      isLast,
      isFirst,
      isBetween,
      isSelected,
      isDisabled:
        isDisabled ||
        (props.availableDates &&
          !props.availableDates.includes(date.formatted)),
      isMarked,
    };
  };

  watch(
    () => props.prices,
    () => {
      calculateCheapestPricePerMonth(props.prices);
    },
  );

  // Intentionally disable since vue-renderless does not have types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cropMonths = (months: Array<any>) => {
    const filtered = props.startDate
      ? months.filter(month => {
          if (month.year > props.startDate.year) {
            return true;
          }

          return (
            month.year === props.startDate.year &&
            month.month >= props.startDate.month
          );
        })
      : months;

    return filtered.slice(0, calendarLazyLoader.visibleItems.value);
  };

  /**
   * Lazy loading took place while a user scrolls the calendar.
   * We need to load 2 months when a user scrolls to observer.
   * Rendering of 2 months may take up to 200ms and load CPU.
   * To prevent FPS drop during scrolling due to the rendering
   * we need to yield rendering to main thread, so scrolling is prioritized
   * @see https://web.dev/optimize-long-tasks/?utm_source=devtools#manually-defer-code-execution
   */
  const onLazyLoaderIntersect = () => {
    defer(calendarLazyLoader.onUpdate);
    defer(calendarLazyLoader.onUpdate);
    emit('onCalendarIntersect', calendarLazyLoader.visibleItems.value);
  };
</script>
